/**
 * Related assets in three groups: what this one is built from (an offer), the platform it is part of (a module), and
 * what uses it (offers built from it, modules that are part of it). Only assets the reader may see are listed.
 */

import { Link } from 'react-router-dom';
import { Network } from 'lucide-react';

import type { AssetOut, RelationOut } from '@/api/types';
import { Badge } from '@/ui/Badge';
import { StateCard } from '@/ui/StateCard';

interface Group {
  key: string;
  title: string;
  rows: RelationOut[];
}

export function relationGroups(relations: RelationOut[]): Group[] {
  const outgoing = (type: string) => relations.filter((r) => r.direction === 'outgoing' && r.type === type);
  return [
    { key: 'built_from', title: 'Built from', rows: outgoing('built_from') },
    { key: 'part_of', title: 'Part of', rows: outgoing('part_of') },
    { key: 'used_by', title: 'Used by', rows: relations.filter((r) => r.direction === 'incoming') },
  ];
}

const USED_AS: Record<string, string> = { built_from: 'built from this asset', part_of: 'part of this asset' };

export function RelatedTab({ asset }: { asset: AssetOut }) {
  const groups = relationGroups(asset.relations);
  if (asset.relations.length === 0) {
    return (
      <StateCard icon={<Network aria-hidden="true" />} title="No related assets">
        <p>Offers list the assets they are built from; modules list the platform they are part of.</p>
      </StateCard>
    );
  }
  return (
    <div className="asset-related">
      {groups.map((g) => (
        <section key={g.key} className="asset-related-group" aria-labelledby={`related-${g.key}`}>
          <h2 id={`related-${g.key}`}>{g.title}</h2>
          {g.rows.length === 0 ? (
            <p className="hint">None</p>
          ) : (
            <ul>
              {g.rows.map((r) => (
                <li key={`${r.type}-${r.asset.id}`}>
                  <Link to={`/assets/${r.asset.id}`}>{r.asset.name}</Link> <Badge variant="kind" value={r.asset.kind} />
                  {r.asset.status !== 'published' && <> <Badge variant="status" value={r.asset.status} /></>}
                  {r.direction === 'incoming' && <span className="hint"> ({USED_AS[r.type] ?? r.type})</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
